import { useSearchParams } from 'react-router-dom';

const searchParamPrefix = 'TasksTable.';

export const useTaskStatusFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const taskStatusFilterValue = (() => {
    const rawValue = searchParams.get(`${searchParamPrefix}status`);
    if (!rawValue || rawValue === '') {
      return undefined;
    }
    return rawValue.split(',') as (
      | 'draft'
      | 'pending'
      | 'queued'
      | 'in_progress'
      | 'completed'
    )[];
  })();

  return {
    taskStatusFilterValue,
    setTaskStatusFilter: (
      filters?: (
        | 'draft'
        | 'pending'
        | 'queued'
        | 'in_progress'
        | 'completed'
      )[],
    ) => {
      setSearchParams(
        (prev) => {
          if (filters === undefined || filters.length === 0) {
            prev.delete(`${searchParamPrefix}status`);
          } else {
            prev.set(`${searchParamPrefix}status`, filters.join(','));
          }
          return prev;
        },
        { replace: true },
      );
    },
  };
};

export const useProjectIdsFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const projectIdsFilterValue = (() => {
    const rawValue = searchParams.get(`${searchParamPrefix}projectIds`);
    if (!rawValue || rawValue === '') {
      return undefined;
    }
    return rawValue.split(',');
  })();

  return {
    projectIdsFilterValue,
    setProjectIdsFilter: (filters?: string[]) => {
      setSearchParams(
        (prev) => {
          if (filters === undefined || filters.length === 0) {
            prev.delete(`${searchParamPrefix}projectIds`);
          } else {
            prev.set(`${searchParamPrefix}projectIds`, filters.join(','));
          }
          return prev;
        },
        { replace: true },
      );
    },
  };
};

export const useTaskIdsFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const taskIdsFilterValue = (() => {
    const rawValue = searchParams.get(`${searchParamPrefix}taskIds`);
    if (!rawValue || rawValue === '') {
      return undefined;
    }
    return rawValue.split(',');
  })();

  return {
    taskIdsFilterValue,
    setTaskIdsFilter: (filters?: string[]) => {
      setSearchParams(
        (prev) => {
          if (filters === undefined || filters.length === 0) {
            prev.delete(`${searchParamPrefix}taskIds`);
          } else {
            prev.set(`${searchParamPrefix}taskIds`, filters.join(','));
          }
          return prev;
        },
        { replace: true },
      );
    },
  };
};

export const useTaskTitleFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const taskTitleFilterValue =
    searchParams.get(`${searchParamPrefix}title`) ?? '';

  return {
    taskTitleFilterValue,
    setTaskTitleFilter: (value: string) => {
      setSearchParams(
        (prev) => {
          if (value === '') {
            prev.delete(`${searchParamPrefix}title`);
          } else {
            prev.set(`${searchParamPrefix}title`, value);
          }
          return prev;
        },
        { replace: true },
      );
    },
  };
};

export const useIsFilterOpen = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isFilterOpen =
    searchParams.get(`${searchParamPrefix}isFilterOpen`) === 'true';

  return {
    isFilterOpen,
    setIsFilterOpen: (isOpen: boolean) => {
      setSearchParams(
        (prev) => {
          if (isOpen) {
            prev.set(`${searchParamPrefix}isFilterOpen`, 'true');
          } else {
            prev.delete(`${searchParamPrefix}isFilterOpen`);
          }
          return prev;
        },
        { replace: true },
      );
    },
  };
};

export const usePageSizeFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSizeRaw = searchParams.get(`${searchParamPrefix}pageSize`);
  const pageSizeFilterValue = pageSizeRaw
    ? Number.parseInt(pageSizeRaw, 10)
    : 10;

  return {
    pageSizeFilterValue,
    setPageSizeFilter: (value: number | string) => {
      setSearchParams(
        (prev) => {
          const numValue =
            typeof value === 'string' ? parseInt(value, 10) : value;
          if (numValue === 10) {
            prev.delete(`${searchParamPrefix}pageSize`);
          } else {
            prev.set(`${searchParamPrefix}pageSize`, value.toString());
          }
          return prev;
        },
        { replace: true },
      );
    },
  };
};

export const usePageFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFilterRaw = searchParams.get(`${searchParamPrefix}page`);
  const pageFilterValue = pageFilterRaw
    ? Number.parseInt(pageFilterRaw, 10)
    : 1;

  return {
    pageFilterValue,
    setPageFilter: (value: number) => {
      setSearchParams(
        (prev) => {
          if (value === 1) {
            prev.delete(`${searchParamPrefix}page`);
          } else {
            prev.set(`${searchParamPrefix}page`, value.toString());
          }
          return prev;
        },
        { replace: true },
      );
    },
  };
};

export const useSortFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const sortByFilterValue =
    searchParams.get(`${searchParamPrefix}sortBy`) ?? 'createdAt';
  const sortDirectionFilterValue =
    searchParams.get(`${searchParamPrefix}sortDirection`) ?? 'desc';

  return {
    sortByFilterValue: sortByFilterValue as 'createdAt' | 'updatedAt',
    sortDirectionFilterValue: sortDirectionFilterValue as 'asc' | 'desc',
    setSortByFilter: (value: 'createdAt' | 'updatedAt') => {
      setSearchParams(
        (prev) => {
          prev.set(`${searchParamPrefix}sortBy`, value);
          return prev;
        },
        { replace: true },
      );
    },
    setSortDirectionFilter: (value: 'asc' | 'desc') => {
      setSearchParams(
        (prev) => {
          prev.set(`${searchParamPrefix}sortDirection`, value);
          return prev;
        },
        { replace: true },
      );
    },
  };
};
